import { useCallback, useEffect, useState } from 'react';
import { api } from '../../../api/api';
import AsyncStorage from '@react-native-async-storage/async-storage';
const initialConversation = {
  messages: [],
  buttons: [],
  state: 'ENDED',
  canUndo: false
};
const errorConversation = {
  ...initialConversation,
  messages: ['Oops, something went wrong, please try again']
};
const useBotActions = _ref => {
  let {
    apiUri,
    token,
    userId,
    organizationId
  } = _ref;
  const apiInstance = useCallback(() => api(apiUri, token), [apiUri, token]);
  const [intent, setIntent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [animationsEnabled, setAnimationsEnabled] = useState(false);
  const [conversation, setConversation] = useState(initialConversation);
  const processConversation = async (botOutput, conversationState) => {
    setConversation(() => ({
      messages: [],
      buttons: [],
      state: conversationState,
      canUndo: false
    }));
    const conversationMessages = [];
    const conversationQuickReplies = [];
    botOutput.conversationOutputs?.forEach(message => {
      message.output && message.output.length && message.output.forEach(text => {
        conversationMessages.push(text.text);
      });
    });
    botOutput.conversationOutputs?.forEach(message => {
      message.quickReplies && message.quickReplies.length && message.quickReplies.forEach(text => {
        conversationQuickReplies.push(text.value);
      });
    });
    const output = {
      messages: conversationMessages,
      buttons: conversationQuickReplies,
      state: conversationState,
      canUndo: botOutput.undoAvailable
    };
    setConversation(output);
  };
  const toggleOptions = () => {
    setShowOptions(!showOptions);
  };
  const toggleAnimation = async () => {
    const newState = !animationsEnabled;
    await AsyncStorage.setItem('boAnimationsEnabled', newState.toString());
    setAnimationsEnabled(newState);
    if (intent && userId) {
      try {
        setLoading(true);
        const loadBotConversation = await apiInstance().bo().getConversation({
          intent,
          userId
        });
        processConversation(loadBotConversation, loadBotConversation.conversationState);
      } catch (e) {
        setConversation(errorConversation);
      } finally {
        setLoading(false);
      }
    }
  };
  const getConversationData = useCallback(async data => {
    if (intent) {
      try {
        setLoading(true);
        const loadBotConversation = await apiInstance().bo().getConversation({
          intent: data || intent,
          userId
        });
        if (loadBotConversation) {
          processConversation(loadBotConversation, loadBotConversation.conversationState);
        }
      } catch (e) {
        setConversation(errorConversation);
      } finally {
        setLoading(false);
      }
    }
  }, [apiInstance, intent, userId]);
  const selectConversationStep = async message => {
    if (intent) {
      try {
        setLoading(true);
        const sendMessageToBot = await apiInstance().bo().updateConversation({
          intent,
          step: message,
          userId,
          organizationId
        });
        processConversation(sendMessageToBot, sendMessageToBot.conversationState);
      } catch (e) {
        setConversation(errorConversation);
      } finally {
        setLoading(false);
      }
    }
  };
  const undoConversation = async () => {
    if (intent) {
      try {
        setLoading(true);
        await apiInstance().bo().undoConversation({
          intent,
          userId
        });
        getConversationData();
      } catch (e) {
        setConversation(errorConversation);
      } finally {
        setLoading(false);
      }
    }
  };
  const resetConversation = async () => {
    if (intent) {
      try {
        setLoading(true);
        setConversation(initialConversation);
        await apiInstance().bo().resetConversation({
          intent,
          userId
        });
        getConversationData(intent);
      } catch (e) {
        setConversation(errorConversation);
      } finally {
        setLoading(false);
      }
    }
  };
  const endConversation = async () => {
    if (intent) {
      try {
        setLoading(true);
        await apiInstance().bo().resetConversation({
          intent,
          userId
        });
      } catch (e) {
        setConversation(errorConversation);
      } finally {
        setLoading(false);
      }
    }
  };
  const setDefaultAnimation = async () => {
    const animation = (await AsyncStorage.getItem('boAnimationsEnabled')) === 'true';
    setAnimationsEnabled(animation);
  };
  useEffect(() => {
    setDefaultAnimation();
  }, []);
  useEffect(() => {
    if (intent) {
      getConversationData();
    }
  }, [intent, getConversationData]);
  return {
    conversation,
    intent,
    animationsEnabled,
    loading,
    setIntent,
    resetConversation,
    undoConversation,
    selectConversationStep,
    toggleAnimation,
    toggleOptions,
    endConversation
  };
};
export default useBotActions;
//# sourceMappingURL=useBotActions.js.map